import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Trophy, TShirt, CalendarBlank, EnvelopeSimple, ArrowRight, X, CheckCircle, Gift } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const GOLD = "#D4AF37";

function Countdown({ target }) {
  const [txt, setTxt] = useState("");
  useEffect(() => {
    const tick = () => {
      const diff = new Date(target) - new Date();
      if (diff <= 0) { setTxt(""); return; }
      const d = Math.floor(diff / 8.64e7), h = Math.floor((diff % 8.64e7) / 3.6e6), m = Math.floor((diff % 3.6e6) / 6e4);
      setTxt(d >= 1 ? `${d}j ${h}h` : `${h}h ${String(m).padStart(2, "0")}min`);
    };
    tick(); const iv = setInterval(tick, 30000); return () => clearInterval(iv);
  }, [target]);
  return txt ? <span>{txt}</span> : null;
}

export const LotteryBanner = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  const [mine, setMine] = useState(null);
  const [showClaim, setShowClaim] = useState(false);
  const [form, setForm] = useState({ team: "", size: "M", note: "" });
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    api.get("/lottery/current").then((r) => setData(r.data)).catch(() => setData(null));
    if (user) api.get("/lottery/my-status").then((r) => setMine(r.data)).catch(() => setMine(null));
  }, [user]);

  const submitClaim = async () => {
    if (!form.team.trim()) { toast.error(t("lottery.claim_team_required")); return; }
    setClaiming(true);
    try {
      const { data: res } = await api.post("/lottery/claim", form);
      setMine((m) => ({ ...(m || {}), claim: res.claim }));
      setShowClaim(false);
      toast.success(t("lottery.claim_done"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("lottery.claim_error"));
    } finally { setClaiming(false); }
  };

  if (!data) return null;
  const isWinner = data.state === "winner";
  const needsVerify = user && user.email_verified === false && !isWinner;

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(t("lottery.verify_sent"));
    } catch {
      toast.error(t("lottery.verify_error"));
    } finally { setSending(false); }
  };

  return (
    <div className="px-5 mt-6">
      <div
        data-testid="lottery-banner"
        className="relative w-full rounded-2xl overflow-hidden border"
        style={{ borderColor: "rgba(212,175,55,0.5)", boxShadow: "0 0 26px rgba(212,175,55,0.22)", minHeight: 232 }}
      >
        {/* Fond composite (maillots génériques + halo doré, identité HitMax) */}
        <div className="absolute inset-0" data-testid="lottery-hero-bg"
          style={{ backgroundImage: "url(/lottery-hero-v2.jpg)", backgroundSize: "cover", backgroundPosition: "center 28%" }} />
        <div className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(8,8,8,0.25) 0%, rgba(8,8,8,0.62) 52%, rgba(8,8,8,0.94) 100%)" }} />
        <div className="absolute inset-x-0 bottom-0 h-1" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />

        <div className="relative flex flex-col justify-end h-full p-4 pt-24">
          <p className="text-[10px] font-black uppercase tracking-[0.22em] gold-text flex items-center gap-1.5">
            <Trophy size={13} weight="fill" color={GOLD} /> {t("lottery.week_kicker")}
          </p>

          {isWinner ? (
            <>
              <p data-testid="lottery-winner-name" className="font-display text-2xl font-black leading-tight mt-1" style={{ color: GOLD }}>
                {t("lottery.winner_headline")}
              </p>
              <p className="text-sm font-bold text-white -mt-0.5">{data.winner?.name}</p>
              <p className="text-[11px] text-white/70 mt-1">{t("lottery.winner_desc")}</p>
              {mine?.is_winner && (
                mine.claim ? (
                  <p data-testid="lottery-claimed" className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-bold" style={{ color: "#17E37E" }}>
                    <CheckCircle size={15} weight="fill" /> {t("lottery.claimed", { team: mine.claim.team })}
                  </p>
                ) : (
                  <button data-testid="lottery-claim-btn" onClick={() => setShowClaim(true)}
                    className="mt-2.5 self-start inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-[12px] font-black text-black"
                    style={{ background: GOLD, boxShadow: "0 0 16px rgba(212,175,55,0.4)" }}>
                    <Gift size={14} weight="fill" /> {t("lottery.claim_cta")}
                  </button>
                )
              )}
            </>
          ) : (
            <>
              <p className="font-display text-xl sm:text-2xl font-black leading-tight text-white mt-1 flex items-center gap-2">
                <TShirt size={22} weight="fill" color={GOLD} /> {t("lottery.prize_headline")}
              </p>
              <p className="text-[11px] text-white/75 mt-1 flex items-center gap-1.5">
                <CalendarBlank size={13} weight="fill" color={GOLD} />
                <span className="font-semibold text-white/90">{t("lottery.each_monday")}</span>
                {data.draw_at && <span className="text-white/60">· {t("lottery.next_in")} <span className="text-white font-semibold"><Countdown target={data.draw_at} /></span></span>}
              </p>
            </>
          )}

          {needsVerify && (
            <div className="mt-3 rounded-xl px-3 py-2 flex items-center gap-2" data-testid="lottery-verify-cta"
              style={{ background: "rgba(0,0,0,0.45)", border: "1px solid rgba(212,175,55,0.35)", backdropFilter: "blur(4px)" }}>
              <EnvelopeSimple size={15} weight="fill" color={GOLD} className="shrink-0" />
              <span className="text-[11px] text-white/80 flex-1">{t("lottery.verify_hint")}</span>
              <button data-testid="lottery-resend-btn" onClick={resend} disabled={sending}
                className="text-[11px] font-bold text-gold hover:underline disabled:opacity-60 shrink-0">
                {sending ? "…" : t("lottery.resend")}
              </button>
            </div>
          )}

          <button
            data-testid="lottery-rules-link"
            onClick={() => navigate("/reglement-tirage")}
            className="mt-3 self-start inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-[12px] font-bold text-black transition-transform hover:scale-[1.03]"
            style={{ background: GOLD, boxShadow: "0 0 16px rgba(212,175,55,0.35)" }}
          >
            {t("lottery.rules_link")} <ArrowRight size={13} weight="bold" />
          </button>
        </div>
      </div>

      {showClaim && createPortal(
        <div className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4 py-6 overflow-y-auto"
          data-testid="claim-modal" onClick={() => setShowClaim(false)}>
          <div className="w-full max-w-sm rounded-3xl p-5 fade-up" style={{ background: "#0B0D0C", border: "1px solid rgba(212,175,55,0.4)" }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl font-black gold-text flex items-center gap-2"><Gift size={20} weight="fill" color={GOLD} /> {t("lottery.claim_title")}</h3>
              <button data-testid="claim-close" onClick={() => setShowClaim(false)} className="text-white/40 hover:text-white"><X size={22} weight="bold" /></button>
            </div>
            <p className="text-[12px] text-white/50 mb-4">{t("lottery.claim_sub")}</p>

            <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">{t("lottery.claim_team")}</label>
            <input data-testid="claim-team" value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })}
              placeholder={t("lottery.claim_team_ph")}
              className="w-full mt-1 mb-3 h-11 rounded-xl px-3 bg-white/5 border border-white/12 text-sm text-white placeholder:text-white/30 outline-none focus:border-gold/60" />

            <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">{t("lottery.claim_size")}</label>
            <select data-testid="claim-size" value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })}
              className="w-full mt-1 mb-3 h-11 rounded-xl px-3 bg-white/5 border border-white/12 text-sm text-white outline-none focus:border-gold/60">
              {["S", "M", "L", "XL", "XXL"].map((s) => <option key={s} value={s} style={{ background: "#111" }}>{s}</option>)}
            </select>

            <label className="text-[11px] font-bold uppercase tracking-wider text-white/50">{t("lottery.claim_note")}</label>
            <textarea data-testid="claim-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={2} placeholder={t("lottery.claim_note_ph")}
              className="w-full mt-1 mb-4 rounded-xl px-3 py-2 bg-white/5 border border-white/12 text-sm text-white placeholder:text-white/30 outline-none focus:border-gold/60 resize-none" />

            <button data-testid="claim-submit" onClick={submitClaim} disabled={claiming}
              className="w-full h-12 rounded-xl font-bold text-sm text-black disabled:opacity-60" style={{ background: GOLD }}>
              {claiming ? "…" : t("lottery.claim_submit")}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
